import { UseFormReturn } from 'react-hook-form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DOCUMENT_CATEGORIES } from '../../../documents.types'
import { DocumentEditFormData } from '../../../documents-upload.types'
import { MarkdownEditor } from '../../markdown-editor'

const FIELD_CLASS = 'h-10 rounded-lg bg-background text-sm shadow-sm border border-border'

type EditDetailsStepProps = {
  form: UseFormReturn<DocumentEditFormData>
}

// Same fields as details-step.tsx (Upload) — kept as a separate concrete component rather than
// a generic one because react-hook-form's Path<T> doesn't resolve usefully through a generic
// form-type parameter; a single title field replaces Upload's per-file title list.
export const EditDetailsStep = ({ form }: EditDetailsStepProps) => {
  const { register, formState } = form
  const errors = formState.errors

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h3 className="text-lg font-semibold">Details</h3>
        <p className="text-sm text-muted-foreground">Update this document&apos;s details.</p>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Title</Label>
        <Input {...register('title')} className={FIELD_CLASS} />
        {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Description</Label>
        <MarkdownEditor
          rows={3}
          placeholder="Short description"
          value={form.watch('descriptionMd') ?? ''}
          onChange={(value) => form.setValue('descriptionMd', value, { shouldDirty: true })}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label>Category</Label>
          <Select
            value={form.watch('category')}
            onValueChange={(value) =>
              // Only ever populated from DOCUMENT_CATEGORIES below, so always a real category.
              form.setValue('category', value as DocumentEditFormData['category'], {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
          >
            <SelectTrigger className={FIELD_CLASS}>
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent>
              {DOCUMENT_CATEGORIES.map((category) => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.category && <p className="text-xs text-destructive">{errors.category.message}</p>}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Date published (if applicable)</Label>
          <Input type="date" {...register('publishedAt')} className={FIELD_CLASS} />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Source</Label>
          <Input
            placeholder="e.g. Christchurch City Council"
            className={FIELD_CLASS}
            {...register('source')}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Organisation</Label>
          <Input
            placeholder="e.g. Christchurch City Council"
            className={FIELD_CLASS}
            {...register('organisation')}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Tags</Label>
        <Input
          placeholder="Comma-separated, e.g. fluvial flooding, liquefaction"
          className={FIELD_CLASS}
          defaultValue={(form.getValues('tags') ?? []).join(', ')}
          onBlur={(e) => {
            const tags = e.target.value
              .split(',')
              .map((tag) => tag.trim())
              .filter(Boolean)
            form.setValue('tags', tags, { shouldDirty: true })
          }}
        />
        <p className="text-xs text-muted-foreground">
          Add keywords so others can find this document. Type to add a new one.
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Notes</Label>
        <MarkdownEditor
          rows={2}
          placeholder="Internal notes"
          value={form.watch('notesMd') ?? ''}
          onChange={(value) => form.setValue('notesMd', value, { shouldDirty: true })}
        />
      </div>
    </div>
  )
}
